import { useRef, useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { api } from '../api/client';
import { useSettings } from '../context/SettingsContext';
import { useT } from '../i18n';

export function LogoSettings() {
  const { t } = useT();
  const { logoUrl, refresh } = useSettings();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPick = () => fileInput.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    if (file.type !== 'image/png') {
      setError(t('admin.logo.errPng'));
      return;
    }
    if (file.size > 1.5 * 1024 * 1024) {
      setError(t('admin.logo.errSize'));
      return;
    }
    setBusy(true);
    try {
      const buffer = await file.arrayBuffer();
      // Convert to base64 (chunked to avoid stack overflow on large files).
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any);
      }
      const data = btoa(binary);
      await api.post('/api/admin/logo', { data });
      await refresh();
    } catch (err: any) {
      setError(String(err?.message ?? err));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onRemove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.del('/api/admin/logo');
      await refresh();
    } catch (err: any) {
      setError(String(err?.message ?? err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="logo-settings">
      <h3 style={{ margin: '0 0 8px 0' }}>{t('admin.logo.title')}</h3>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 16px 0' }}>
        {t('admin.logo.help')}
      </p>

      <div className="logo-settings-row">
        <div className="logo-preview">
          {logoUrl
            ? <img src={logoUrl} alt={t('admin.logo.title')} />
            : <span className="logo-placeholder">{t('admin.logo.none')}</span>}
        </div>
        <div className="logo-actions">
          <input
            ref={fileInput}
            type="file"
            accept="image/png"
            style={{ display: 'none' }}
            onChange={onFile}
          />
          <button className="btn btn-accent" onClick={onPick} disabled={busy}>
            <Upload size={14} /> {busy ? t('common.saving') : t('admin.logo.upload')}
          </button>
          {logoUrl && (
            <button className="btn" onClick={onRemove} disabled={busy}>
              <Trash2 size={14} /> {t('admin.logo.remove')}
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ marginTop: 8, color: 'var(--err)', fontSize: 12 }}>{error}</div>}
    </div>
  );
}
